import type { CageActiveFishGroup, UpdateCageActiveFishGroupRequest } from '@/domain/fishGroups';

/** The form keeps the count as typed, so an empty input stays apart from a number. */
export interface ActiveFishGroupFormValues {
    name: string;
    fishCount: string;
    reason: string;
}

export function toFormValues(group: CageActiveFishGroup): ActiveFishGroupFormValues {
    return {
        name: group.name,
        fishCount: group.initialPopulationSize === null ? '' : String(group.initialPopulationSize),
        reason: '',
    };
}

/**
 * Carries refreshed group data into the form: a field still showing the old stored value takes
 * the new one, while a field the user edited, and the reason, stay as typed. Without it a
 * refetch that corrected the count would leave the old count on screen, and saving a rename
 * would send it back.
 */
export function syncUntouchedFields(
    values: ActiveFishGroupFormValues,
    previous: CageActiveFishGroup,
    next: CageActiveFishGroup
): ActiveFishGroupFormValues {
    const from = toFormValues(previous);
    const to = toFormValues(next);
    return {
        name: values.name === from.name ? to.name : values.name,
        fishCount: values.fishCount === from.fishCount ? to.fishCount : values.fishCount,
        reason: values.reason,
    };
}

/** Empty is "unknown" (`null`); anything else must be a whole number above zero. */
export function parseFishCount(value: string): number | null | undefined {
    const trimmed = value.trim();
    if (trimmed === '') return null;
    if (!/^\d+$/.test(trimmed)) return undefined;
    const count = Number(trimmed);
    return count >= 1 && Number.isSafeInteger(count) ? count : undefined;
}

export function isFishCountValid(value: string): boolean {
    return parseFishCount(value) !== undefined;
}

/**
 * The correction to send: only the fields that differ from the stored group, plus the reason.
 * `null` when nothing would change or a value is invalid, since a reason alone corrects nothing.
 */
export function buildActiveFishGroupRequest(
    group: CageActiveFishGroup,
    values: ActiveFishGroupFormValues
): UpdateCageActiveFishGroupRequest | null {
    const name = values.name.trim();
    const fishCount = parseFishCount(values.fishCount);
    const reason = values.reason.trim();
    if (name === '' || fishCount === undefined || reason === '') return null;

    const request: UpdateCageActiveFishGroupRequest = { reason };
    if (name !== group.name) request.name = name;
    if (fishCount !== group.initialPopulationSize) request.initialPopulationSize = fishCount;

    return request.name === undefined && request.initialPopulationSize === undefined
        ? null
        : request;
}
